import { EPropertySettingTypes } from 'api/meta/enums/nodes/properties/EPropertySettingTypes';
import type { PropertySettings } from 'state/selectedTemplate/selectedTemplateStore';
import { EPropertyEditTypes, EPropertyTypes } from 'state/selectedTemplate/selectedTemplateStore';
import type { Nullable } from 'utils/values';

/**
 * Resolves which input a property value is edited with, for everything that has the property's
 * `PropertySettings` to hand — previously duplicated in both node-details panels and in
 * useFetchTemplateNodeProperties. The grid cells still resolve their own edit type from a column
 * spec (`nodeQuery/grid/cells/propertyCell/hooks/useCellEditType.ts`) and should be pointed here once
 * they can reach the settings.
 *
 * The setting type decides on its own wherever it can. Only Calculated has no intrinsic shape (the
 * query behind it can return anything), so it falls back to the native Neo4j type — as would a
 * setting type this switch has not been taught yet.
 */
export function getPropertyEditType(settings: Nullable<PropertySettings>): EPropertyEditTypes {
  if (!settings) {
    return EPropertyEditTypes.Unknown;
  }

  switch (settings.$type) {
    case EPropertySettingTypes.AutoId:
      return EPropertyEditTypes.AutoId;
    case EPropertySettingTypes.Ownership:
      return EPropertyEditTypes.Ownership;
    case EPropertySettingTypes.Picklist:
      return EPropertyEditTypes.Picklist;
    case EPropertySettingTypes.Date:
      return EPropertyEditTypes.Date;
    case EPropertySettingTypes.Text:
      return EPropertyEditTypes.Text;
    case EPropertySettingTypes.Rational:
    case EPropertySettingTypes.Integer:
      return EPropertyEditTypes.Number;
    // Calculated is the one setting type with no intrinsic shape, so it resolves from the native
    // type — and so does any type added backend-side later, which defaulting to Unknown would
    // instead render read-only in both panels.
    default:
      return getEditTypeOfValueType(settings.propertyType);
  }
}

/** Fallback for property settings that do not pin down a shape of their own. */
function getEditTypeOfValueType(propertyType: EPropertyTypes): EPropertyEditTypes {
  switch (propertyType) {
    case EPropertyTypes.Long:
    case EPropertyTypes.Double:
      return EPropertyEditTypes.Number;
    case EPropertyTypes.Date:
      return EPropertyEditTypes.Date;
    default:
      return EPropertyEditTypes.Text;
  }
}

/**
 * Values the system generates and a user can therefore never type: an Auto-ID is assigned on create,
 * a Calculated value is derived from its single-value query. Both still render their normal input,
 * disabled, so the value stays readable.
 *
 * Ownership is deliberately absent — it is read-only in grid columns (see
 * `features/library-nodes/constants.ts`) but editable through the picker in the details panels.
 */
export function isPropertyValueReadOnly(settings: Nullable<PropertySettings>): boolean {
  return settings?.$type === EPropertySettingTypes.AutoId || settings?.$type === EPropertySettingTypes.Calculated;
}
